import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const navItems = [
  { label: "Home", href: "#home" },
  { label: "About", href: "#about" },
  { label: "Story", href: "#story" },
  { label: "Characters", href: "#characters" },
  { label: "Gallery", href: "#gallery" },
  { label: "Contact", href: "#contact" },
];

const routeItems = [
  { label: "Lore", href: "/lore", icon: "ri-book-open-line" },
  { label: "Book", href: "/book", icon: "ri-quill-pen-line" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [active, setActive] = useState("home");
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);

      const sections = navItems.map((item) => item.href.replace("#", ""));
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i]);
        if (el && window.scrollY >= el.offsetTop - 120) {
          setActive(sections[i]);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href: string) => {
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#8B1A1A]/30 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        {/* Logo */}
        <button
          onClick={() => handleNav("#home")}
          className="flex items-center gap-2 cursor-pointer"
        >
          <div className="w-8 h-8 flex items-center justify-center">
            <i className="ri-sword-line text-[#C8922A] text-xl" />
          </div>
          <span className="font-['Special_Elite'] text-white text-lg tracking-[0.15em] uppercase">
            The <span className="text-[#C8922A]">Wraith</span>
          </span>
        </button>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-8">
          {navItems.map((item) => {
            const id = item.href.replace("#", "");
            return (
              <li key={item.label}>
                <button
                  onClick={() => handleNav(item.href)}
                  className={`text-xs tracking-[0.2em] uppercase font-['Share_Tech_Mono'] transition-all duration-300 cursor-pointer whitespace-nowrap
                    ${active === id ? "text-[#C8922A] border-b border-[#C8922A] pb-0.5" : "text-[#9A9A9A] hover:text-white"}`}
                >
                  {item.label}
                </button>
              </li>
            );
          })}
          {routeItems.map((item) => (
            <li key={item.label}>
              <button
                onClick={() => navigate(item.href)}
                className="text-xs tracking-[0.2em] uppercase font-['Share_Tech_Mono'] transition-all duration-300 cursor-pointer whitespace-nowrap text-[#9A9A9A] hover:text-white flex items-center gap-1.5"
              >
                <i className={`${item.icon} text-sm`} />
                {item.label}
              </button>
            </li>
          ))}
        </ul>

        {/* Read Book + Signal Buttons */}
        <div className="hidden md:flex items-center gap-3">
          <button
            onClick={() => navigate("/book")}
            className="flex items-center gap-2 border border-[#C8922A]/50 hover:border-[#C8922A] hover:bg-[#C8922A]/10 text-[#C8922A] text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-quill-pen-line text-sm" />
            Read The Book
          </button>
          <button
            onClick={() => handleNav("#contact")}
            className="flex items-center gap-2 bg-[#8B1A1A]/20 border border-[#8B1A1A] hover:bg-[#8B1A1A] text-white text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-radio-line text-sm" />
            Signal
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="md:hidden w-8 h-8 flex items-center justify-center text-white cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0A0A0A]/98 border-t border-[#8B1A1A]/30 px-6 py-4 flex flex-col gap-4">
          {navItems.map((item) => (
            <button
              key={item.label}
              onClick={() => handleNav(item.href)}
              className="text-[#9A9A9A] hover:text-[#C8922A] text-xs tracking-[0.2em] uppercase font-['Share_Tech_Mono'] text-left transition-colors cursor-pointer"
            >
              {item.label}
            </button>
          ))}
          {routeItems.map((item) => (
            <button
              key={item.label}
              onClick={() => { navigate(item.href); setMenuOpen(false); }}
              className="text-[#C8922A] text-xs tracking-[0.2em] uppercase font-['Share_Tech_Mono'] text-left transition-colors cursor-pointer flex items-center gap-2"
            >
              <i className={`${item.icon} text-sm`} />
              {item.label}
            </button>
          ))}
          <div className="border-t border-[#1E1E1E] pt-4">
            <button
              onClick={() => { handleNav("#contact"); }}
              className="flex items-center gap-2 bg-[#8B1A1A]/20 border border-[#8B1A1A] hover:bg-[#8B1A1A] text-white text-xs tracking-[0.15em] uppercase font-['Share_Tech_Mono'] px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap w-full justify-center"
            >
              <i className="ri-radio-line text-sm" />
              Signal
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
